import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import LeatherbackImage1 from "@assets/turtle-images/leatherback-1.webp";
import LeatherbackImage2 from "@assets/turtle-images/leatherback-2.webp";
import LeatherbackImage3 from "@assets/turtle-images/leatherback-3.webp";
import LeatherbackImage4 from "@assets/turtle-images/leatherback-4.webp";

interface LeatherbackSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function LeatherbackSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: LeatherbackSectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Leatherback turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden bg-white py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          {/* Title */}
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
            Leatherback turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic" >Dermochelys coriacea</h3>
        </div>

        {/* Images section with model viewer and AR button */}
        <div className="flex flex-col gap-6 sm:gap-[30px]">
          {/* Model Viewer */}
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <a
              href="https://sketchfab.com/3d-models/model-95a-adult-leatherback-sea-turtle-mac-4974c93644a24da280fde68cba74a12d"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black text-[12px] font-light leading-[1.2em] italic underline hover:opacity-70"
            >
              sketchfab
            </a>
          </div>

          {/* AR button */}
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        {/* Content Sections */}
        <div className="flex flex-col gap-10 sm:gap-[50px]">
          {/* Row 1: Deep-sea diver + Long-distance traveller */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            {/* Left: Deep-sea diver */}
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full h-auto bg-gray-300">
                  <img
                    src={LeatherbackImage1}
                    width={1090}
                    height={530}
                    alt="Leatherback turtle deep-sea diver"
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                {/* Empty caption spacer to align with adjacent column */}
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Leatherbacks visiting our coast nest on the beaches of countries to our north.
                </p>
              </div>
              <h3
                className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  lineHeight: "1.2em",
                }}
              >
                Deep-sea diver
              </h3>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  Leatherbacks dive deeper than any other reptile – up to 1.3 km – and can stay underwater for up to 90 minutes.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  Unlike other sea turtles, the leatherback’s rubbery shell is made of small, interlocking bones covered by tough leathery skin. This helps their shell to contract and expand as they dive deep and then rise back to the surface.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                 A thick layer of fat keeps leatherbacks warm as they dive into deep, cooler waters.
                </p>
              </div>
            </div>

            {/* Right: Long-distance traveller */}
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full h-auto bg-gray-300">
                  <img
                    src={LeatherbackImage2}
                    width={1090}
                    height={530}
                    alt="Leatherback turtle long-distance traveller"
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                <p
                  className="text-black text-[12px] font-light leading-[1.2em] italic"
                >
                  Leatherbacks visiting our coast nest on the beaches of countries to our north.
                  Data source: <a href="https://arcg.is/1eXqWO0" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">TurtleNet: Turtle Nesting Distribution abundance &amp; Migration</a>. Data provided in partnership by Queensland Government Department of Environment, Science, &amp; Innovation and Convention on Migratory Species (CMS). ArcGIS Online.
                </p>
              </div>
              <h3
                className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  lineHeight: "1.2em",
                }}
              >
                Long-distance traveller
              </h3>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  Leatherbacks are long-distance travellers: one tagged turtle swam 20,000 km between the USA and Indonesia in less than 2 years.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  All sea turtles move between places to feed, nest and breed. Even our Australian-only local, the flatback, moves along the Queensland coast.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  The leatherbacks visit our coast in summer to feast on jellyfish that thrive in Queensland’s nutrient-rich ocean before travelling to warmer beaches in Indonesia, Papua New Guinea and the Solomon Islands to nest.
                </p>
              </div>
            </div>
          </div>

          {/* Row 2: Jagging jellyfish + Rare visitor to the coast */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            {/* Left: Jagging jellyfish */}
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full h-auto bg-gray-300">
                  <img
                    src={LeatherbackImage3}
                    width={1090}
                    height={530}
                    alt="Leatherback turtle eating jellyfish"
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                {/* Empty caption spacer to align with adjacent column */}
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by Georgina Hume
                </p>
              </div>
              <h3
                className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  lineHeight: "1.2em",
                }}
              >
                Jagging jellyfish
              </h3>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  All sea turtles love to eat jellyfish; it’s like chocolate to them! Leatherbacks especially love large, soft jellyfish like lion’s mane and moon jellyfish.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  It’s a one-way trip for a jellyfish: sea turtle throats have special backward-facing spines (papillae) that stop slippery jellyfish and other food from escaping.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  Those spines can be a fatal trap if plastic rubbish, which looks and smells just like jellyfish, gets stuck in them.
                </p>
              </div>
            </div>

            {/* Right: Rare visitor to the coast */}
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full h-auto bg-gray-300">
                  <img
                    src={LeatherbackImage4}
                    width={1090}
                    height={530}
                    alt="Leatherback turtle rare coast visitor"
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                <p
                  className="text-black text-[12px] font-light leading-[1.2em] italic"
                >
                  Photo by Georgina Hume
                </p>
              </div>
              <h3
                className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  lineHeight: "1.2em",
                }}
              >
                Rare visitor to the coast
              </h3>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  Despite living in our oceans almost since the age of dinosaurs, spotting a leatherback on our Fraser Coast is rare.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  You won’t see a leatherback off the beach: they feed offshore and it’s been more than 30 years since they nested on Australian beaches. Why? We are not sure.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  The last known leatherback to nest on mainland Australia was in 1996 between Agnes Waters and Bundaberg.
                </p>
                <p
                  className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 300,
                    lineHeight: "1.2em",
                  }}
                >
                  UniSC dolphin researcher, Georgina Hume, spotted the leatherback in the photo on a research trip in Hervey Bay.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
